import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Brand } from '@/components/Brand';
import { useAuth } from '@/context/AuthContext';
import { roleHome } from '@/lib/format';

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (params.get('erro') === 'google') toast.error('Falha no login com Google. Tente novamente.');
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success('Bem-vindo(a)!');
      navigate(roleHome(u.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1540541338287-41700207dee6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200" alt="Viagem" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <h2 className="font-display text-3xl font-semibold">Sandra Tur</h2>
          <p className="mt-2 text-primary-foreground/90 max-w-sm">Acesse sua conta para gerenciar viagens, clientes e contratos.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 hero-wash">
        <div className="w-full max-w-sm">
          <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" data-testid="login-back-to-site">
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao site</Link>
          </Button>
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Brand />
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Entrar</h1>
          <p className="mt-2 text-sm text-muted-foreground">Bem-vindo de volta. Acesse o seu painel.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@email.com" className="pl-9" data-testid="auth-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9" data-testid="auth-password-input" />
              </div>
            </div>
            <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="auth-submit-button">
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            Não tem conta? <Link to="/register" className="text-primary font-medium hover:underline">Criar conta</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
